<!-- LiveTranscript.vue with Natural Speech Pauses and Citation Handling -->
<template>
    <div class="live-transcript-overlay" v-if="displayText && isVisible">
      <div class="live-transcript-container">
        <!-- Speaker avatar with wave animation -->
        <div class="transcript-speaker-avatar">
          <img
            v-if="speakerImage"
            :src="speakerImage"
            :alt="currentSpeaker"
            class="avatar-image"
            @error="handleImageError"
          />
          <div v-else class="avatar-fallback">
            {{ getSpeakerInitials() }}
          </div>

          <!-- Wave animation -->
          <div class="avatar-wave-indicator" :class="{ 'active': !isPaused }">
            <div v-for="n in 3" :key="`wave-${n}`" class="wave-bar"></div>
          </div>
        </div>

        <div class="transcript-content">
          <div class="transcript-text">
            <span v-html="typewriterText"></span>
            <span class="cursor" :class="{ 'blink': cursorBlinking }">|</span>
          </div>
          <div class="transcript-speaker" v-if="showSpeaker">{{ currentSpeaker }}</div>
        </div>
      </div>
    </div>
  </template>

  <script>
  export default {
    name: 'LiveTranscript',
    props: {
      // Current segment text being spoken
      text: {
        type: String,
        default: ''
      },
      // Speaker name
      speaker: {
        type: String,
        default: ''
      },
      // Speaker image URL
      speakerImage: {
        type: String,
        default: ''
      },
      // Whether to show the transcript
      visible: {
        type: Boolean,
        default: true
      },
      // Whether to show the speaker name
      showSpeaker: {
        type: Boolean,
        default: true
      },
      // Maximum length before truncating text
      maxLength: {
        type: Number,
        default: 400
      },
      // Base typing speed in milliseconds per character
      typingSpeed: {
        type: Number,
        default: 45
      },
      // Whether to restart typing animation when text changes
      restartOnTextChange: {
        type: Boolean,
        default: true
      },
      // Voice playback speed (1.0 = normal)
      voiceSpeed: {
        type: Number,
        default: 1.0
      }
    },
    data() {
      return {
        isVisible: this.visible,
        currentSpeaker: this.speaker,
        fullText: this.text,
        displayText: '',
        typewriterText: '',
        typingIndex: 0,
        typingTimer: null,
        cursorBlinking: false,
        cursorTimer: null,
        isPaused: false,
        pauseTimer: null,
        // Punctuation that indicates pauses with relative pause length multipliers
        pausePunctuation: {
          '.': 10,  // End of sentence - longest pause
          '!': 10,  // Exclamation
          '?': 10,  // Question
          ',': 5,  // Comma - medium pause
          ';': 5,  // Semicolon
          ':': 5,  // Colon
          '-': 5,  // Dash - short pause
          '—': 5,  // Em dash
          '(': 2,  // Opening parenthesis
          ')': 2,  // Closing parenthesis
          '\n': 5, // Line break - long pause
          '\r': 5  // Carriage return
        },
        // Regular expressions for special text patterns
        regexPatterns: {
          // Match citation patterns like **c3**, **c4**
          citation: /\*\*c\d+\*\*/g,
          // Match common abbreviations with periods
          abbreviation: /(?:\b[A-Z]\.)+(?:[A-Z]\b|[A-Z]\.)/g
        }
      }
    },
    watch: {
      text: {
        immediate: true,
        handler(newText) {
          this.fullText = newText;
          this.processText();

          // Restart or continue typewriter effect
          if (this.restartOnTextChange) {
            this.resetTypewriter();
          }
        }
      },
      speaker(newSpeaker) {
        this.currentSpeaker = newSpeaker;
      },
      visible(newVisible) {
        this.isVisible = newVisible;

        // Stop typewriter when hidden
        if (!newVisible) {
          this.stopTypewriter();
        } else if (this.displayText) {
          // Resume typewriter when shown
          this.startTypewriter();
        }
      },
      voiceSpeed() {
        // Reset typewriter when speed changes to adjust typing speed
        if (this.typingIndex < this.displayText.length) {
          this.resetTypewriter();
        }
      }
    },
    mounted() {
      // Start the cursor blinking animation
      this.startCursorBlink();

      // Start typewriter if we have text
      if (this.displayText) {
        this.startTypewriter();
      }
    },
    beforeDestroy() {
      // Clean up timers
      this.stopTypewriter();
      this.stopCursorBlink();
      this.clearPauseTimer();
    },
    methods: {
      // Handle image loading errors
      handleImageError(event) {
        // Replace with fallback
        event.target.style.display = 'none';
        const container = event.target.parentNode;

        // Create fallback with initials
        const fallback = document.createElement('div');
        fallback.className = 'avatar-fallback';
        fallback.textContent = this.getSpeakerInitials();
        container.appendChild(fallback);
      },

      // Get speaker initials for fallback
      getSpeakerInitials() {
        if (!this.currentSpeaker) return '?';
        return this.currentSpeaker
          .split(' ')
          .map(word => word.charAt(0))
          .join('')
          .toUpperCase()
          .substring(0, 2);
      },

      // Process text to display
      processText() {
        // Clean and truncate the text if necessary
        if (!this.fullText) {
          this.displayText = '';
          return;
        }

        // Remove HTML tags
        let cleanText = this.fullText.replace(/<[^>]*>/g, '');

        // Remove citation markers if any (like [1], [2])
        cleanText = cleanText.replace(/\[\d+\]/g, '');

        // Remove redundant spaces
        cleanText = cleanText.replace(/\s+/g, ' ').trim();

        // Truncate if necessary
        if (cleanText.length > this.maxLength) {
          cleanText = cleanText.substring(0, this.maxLength) + '...';
        }

        this.displayText = cleanText;
      },

      // Start the typewriter effect
      startTypewriter() {
        // Clear any existing timer
        this.stopTypewriter();
        this.clearPauseTimer();
        this.isPaused = false;

        // If already at the end, nothing to do
        if (this.typingIndex >= this.displayText.length) {
          this.cursorBlinking = true;
          return;
        }

        // Start the typing animation
        this.typeNextCharacter();
      },

      // Type next character with intelligent pauses
     // Type next character with intelligent pauses
    typeNextCharacter() {
        // If we've reached the end of the text, stop typing
        if (this.typingIndex >= this.displayText.length) {
            this.cursorBlinking = true;
            this.isPaused = true;
            return;
        }

        // Type the next character
        this.typewriterText += this.displayText.charAt(this.typingIndex);

        // Check if we should pause after this character
        let shouldPause = false;
        let pauseMultiplier = 0;

        // Get current character and surrounding context
        const currentChar = this.displayText.charAt(this.typingIndex);
        const nextChar = this.typingIndex + 1 < this.displayText.length ? this.displayText.charAt(this.typingIndex + 1) : '';
        const prevChar = this.typingIndex > 0 ? this.displayText.charAt(this.typingIndex - 1) : '';

        // Look ahead for citation patterns (like c1c6)
        const lookAhead = this.typingIndex + 10 < this.displayText.length ?
            this.displayText.substring(this.typingIndex + 1, this.typingIndex + 10) :
            this.displayText.substring(this.typingIndex + 1);

        // Check if we're about to start a citation (about to type 'c' with digit after it)
        const isStartingCitation = currentChar === 'c' && /^\d/.test(nextChar);

        // Check if we've just finished a citation (just typed a digit after 'c' with no more digits ahead)
        const isEndingCitation = /\d/.test(currentChar) && prevChar && /c\d+c/.test(this.displayText.substring(this.typingIndex - 4, this.typingIndex + 1)) && !/\d/.test(nextChar);

        // Check for punctuation pauses
        if (this.pausePunctuation[currentChar]) {
            // Extract some context to check for special cases
            const contextAfter = this.displayText.substring(this.typingIndex + 1, this.typingIndex + 15);

            // Check if this is a period in an abbreviation (like B.Com)
            const isAbbreviation = /^[A-Z]\.([A-Z]|[a-z])/.test(prevChar + currentChar + nextChar);

            // Check if this is followed by a citation marker
            const followedByCitation = /^\s*c\d+/.test(contextAfter);

            // Only pause for periods that are sentence endings, not abbreviations
            if (currentChar === '.' && (isAbbreviation || nextChar === ' ' && /[A-Z]/.test(contextAfter.trim().charAt(0)))) {
            // This is likely a sentence ending period, so pause
            shouldPause = true;
            pauseMultiplier = this.pausePunctuation[currentChar];
            } else if (followedByCitation) {
            // Apply a pause before citation
            shouldPause = true;
            pauseMultiplier = 10; // Shorter pause before citation
            } else {
            // Standard punctuation pause
            shouldPause = true;
            pauseMultiplier = this.pausePunctuation[currentChar];
            }
        }

        // Handle citation starts and ends
        if (isStartingCitation) {
            shouldPause = true;
            pauseMultiplier = 10; // Pause before starting a citation
        } else if (isEndingCitation) {
            shouldPause = true;
            pauseMultiplier = 10; // Pause after ending a citation
        }

        this.typingIndex++;

        // Keep cursor steady during typing
        this.cursorBlinking = false;

        // Apply the appropriate pause if needed
        if (shouldPause && pauseMultiplier > 0) {
            this.addNaturalPause(pauseMultiplier);
        } else {
            // Otherwise continue with next character after the base delay
            this.scheduleNextCharacter();
        }
    },

      // Add a natural pause in the typing
      addNaturalPause(multiplier) {
        // Set paused state for animation
        this.isPaused = true;

        // Calculate pause duration based on punctuation type and speed
        const basePause = this.typingSpeed * multiplier;
        const adjustedPause = basePause / this.voiceSpeed;

        // Schedule the end of the pause
        this.pauseTimer = setTimeout(() => {
          this.isPaused = false;
          this.scheduleNextCharacter();
        }, adjustedPause);
      },

      // Schedule the next character with appropriate timing
      scheduleNextCharacter() {
        // Calculate delay based on speed
        const delay = this.getTypingDelay();

        // Schedule the next character
        this.typingTimer = setTimeout(() => {
          this.typeNextCharacter();
        }, delay);
      },

      // Get appropriate typing delay based on voice speed
      getTypingDelay() {
        // Adjust base typing speed for voice speed
        let delay = this.typingSpeed / this.voiceSpeed;

        // Add slight randomness for natural feel
        const randomFactor = 0.9 + (Math.random() * 0.2);
        delay *= randomFactor;

        return Math.max(10, Math.floor(delay));
      },

      // Stop the typewriter
      stopTypewriter() {
        if (this.typingTimer) {
          clearTimeout(this.typingTimer);
          this.typingTimer = null;
        }
      },

      // Clear any pause timer
      clearPauseTimer() {
        if (this.pauseTimer) {
          clearTimeout(this.pauseTimer);
          this.pauseTimer = null;
        }
      },

      // Reset the typewriter
      resetTypewriter() {
        this.stopTypewriter();
        this.clearPauseTimer();
        this.typingIndex = 0;
        this.typewriterText = '';
        this.isPaused = false;
        this.startTypewriter();
      },

      // Start cursor blinking animation
      startCursorBlink() {
        // Toggle cursor blinking state every 500ms
        this.cursorTimer = setInterval(() => {
          // Only blink cursor when not actively typing
          if (this.typingIndex >= this.displayText.length) {
            this.cursorBlinking = !this.cursorBlinking;
          }
        }, 500);
      },

      // Stop cursor blinking animation
      stopCursorBlink() {
        if (this.cursorTimer) {
          clearInterval(this.cursorTimer);
          this.cursorTimer = null;
        }
      }
    }
  }
  </script>

  <style scoped>
  .live-transcript-overlay {
    position: absolute;
    bottom: 20px;
    left: 0;
    right: 0;
    z-index: 10;
    display: flex;
    justify-content: center;
    pointer-events: none; /* Allow clicks to pass through */
  }

  .live-transcript-container {
    background-color: rgba(0, 0, 0, 0.7);
    border-radius: 8px;
    padding: 10px 16px;
    max-width: 90%;
    display: flex;
    align-items: center;
    gap: 12px;
    animation: fadeIn 0.3s ease-in-out;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  /* Avatar styling */
  .transcript-speaker-avatar {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    position: relative;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid rgba(255, 255, 255, 0.2);
  }

  .avatar-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar-fallback {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.4);
    color: white;
    font-weight: bold;
    font-size: 14px;
  }

  /* Wave animation styling */
  .avatar-wave-indicator {
    position: absolute;
    bottom: -2px;
    right: -2px;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 4px;
    padding: 2px;
    display: flex;
    gap: 2px;
    transition: background-color 0.3s ease;
  }

  .avatar-wave-indicator.active {
    background: rgba(32, 213, 94, 0.7);
  }

  .wave-bar {
    width: 2px;
    height: 2px;
    border-radius: 1px;
    background: white;
    transition: height 0.2s ease;
  }

  .active .wave-bar {
    animation: waveAnimation 0.5s ease-in-out infinite;
  }

  .active .wave-bar:nth-child(1) { animation-delay: -0.3s; }
  .active .wave-bar:nth-child(2) { animation-delay: -0.15s; }
  .active .wave-bar:nth-child(3) { animation-delay: 0s; }

  @keyframes waveAnimation {
    0% { height: 2px; }
    50% { height: 8px; }
    100% { height: 3px; }
  }

  .transcript-content {
    flex: 1;
  }

  .transcript-text {
    color: white;
    font-size: 18px;
    font-weight: 500;
    line-height: 1.4;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    min-height: 1.4em;
  }

  .transcript-speaker {
    color: rgba(255, 255, 255, 0.8);
    font-size: 14px;
    margin-top: 4px;
    font-style: italic;
  }

  .cursor {
    display: inline-block;
    color: white;
    font-weight: bold;
    margin-left: 1px;
    position: relative;
    top: -1px;
  }

  .cursor.blink {
    animation: blink 1s step-end infinite;
  }

  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* Dark theme adjustments */
  .theme--dark .live-transcript-container {
    background-color: rgba(0, 0, 0, 0.8);
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .live-transcript-container {
      max-width: 100%;
    }

    .transcript-speaker-avatar {
      width: 28px;
      height: 28px;
    }

    .transcript-text {
      font-size: 13px;
    }

    .transcript-speaker {
      font-size: 11px;
    }
  }
  </style>
